import { TypedDocumentNode, gql } from '@apollo/client';
import { FC, ReactNode, Suspense } from 'react';
import { Navigate, To, useNavigation } from 'react-router-dom';
import styled from 'styled-components';

import { So5FixtureEvent } from '@sorare/core/src/__generated__/globalTypes';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { OnboardingHighlightWrapper } from '@sorare/core/src/components/onboarding/OnboardingHighlightWrapper';
import { ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_LINEUPS } from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';

import { FixtureSelector } from 'components/fixture/FixtureSelector';
import { getFixtureQueryVariables, getValidEventType } from 'lib/so5';

import type {
  BasicFixtureLayoutQuery,
  BasicFixtureLayoutQueryVariables,
} from './__generated__/index.graphql';

const FIXTURE_LAYOUT_QUERY = gql`
  query BasicFixtureLayoutQuery(
    $type: So5State
    $slug: String
    $eventType: So5FixtureEvent
    $sport: Sport!
  ) {
    so5 {
      ongoingSo5Fixture: so5Fixture(
        type: MY_ONGOING
        eventType: $eventType
        sport: $sport
      ) {
        slug
      }
      so5Fixture(
        type: $type
        eventType: $eventType
        slug: $slug
        sport: $sport
      ) {
        slug
        type
        displayName
        shortDisplayName
        ...FixtureSelector_so5Fixture
      }
    }
  }
  ${FixtureSelector.fragments.so5Fixture}
` as TypedDocumentNode<
  BasicFixtureLayoutQuery,
  BasicFixtureLayoutQueryVariables
>;

const Root = styled(Vertical)`
  isolation: isolate;
  padding-top: var(--triple-unit);
  padding-bottom: var(--triple-unit);
`;

const FixtureSelectorContainer = styled.div`
  margin: auto;
  position: relative;
  max-width: min(100%, 430px);

  &.big {
    max-width: min(100%, 710px);
  }
`;

export const loaderFn = <
  Params extends {
    eventType?: string;
    fixture?: string;
  },
>() => {
  return queryLoader<Params>(
    FIXTURE_LAYOUT_QUERY,
    ({ sport, eventType, fixture }) => {
      return {
        ...getFixtureQueryVariables(fixture),
        eventType: getValidEventType(eventType),
        sport,
      };
    },
    { preventTransitionBeforeLoaded: true }
  );
};

type Props = {
  withOnboarding?: boolean;
  eventType: So5FixtureEvent;
  navigateTo?: (fixture: string) => To;
  pickerSize?: 'small' | 'big';
  className?: string;
  extraHeader?: ReactNode;
  children?:
    | ReactNode
    | FC<React.PropsWithChildren<{ so5FixtureSlug: string }>>;
  onFixtureChanged?: (fixture: string) => void;
};
export const BasicFixtureLayout = ({
  eventType,
  className,
  navigateTo,
  withOnboarding,
  pickerSize = 'small',
  extraHeader,
  children,
  onFixtureChanged,
}: Props) => {
  const navigation = useNavigation();
  const isNavigating = Boolean(navigation.location);
  const { generateSportContextPath } = useSportContext();
  const { data } = useQueryLoader<BasicFixtureLayoutQuery>();
  const isLaptopAndAbove = useIsLaptop();

  const { so5 } = data;
  const { so5Fixture, ongoingSo5Fixture } = so5;

  if (!so5Fixture) {
    return (
      <Navigate
        to={
          navigateTo?.(ongoingSo5Fixture ? 'ongoing' : 'past') ||
          generateSportContextPath(ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_LINEUPS, {
            params: {
              fixture: ongoingSo5Fixture ? 'ongoing' : 'past',
              eventType: eventType.toLowerCase(),
            },
          })
        }
        replace
      />
    );
  }

  return (
    <Root gap={isLaptopAndAbove ? 3 : 2} className={className}>
      <FullWidthContainer>
        <OnboardingHighlightWrapper
          wrap={withOnboarding}
          highlightName="FixtureSelectorContainer"
        >
          <FixtureSelectorContainer className={pickerSize}>
            <FixtureSelector
              withMask
              // Makes sure we dispay the correct selector for daily/classic
              key={so5Fixture.type}
              navigateTo={navigateTo}
              currentFixture={so5Fixture}
              pickerSize={pickerSize}
              onFixtureChanged={onFixtureChanged}
            />
          </FixtureSelectorContainer>
        </OnboardingHighlightWrapper>
      </FullWidthContainer>
      <Suspense fallback={<LoadingIndicator />}>
        {extraHeader}
        {isNavigating ? (
          <LoadingIndicator />
        ) : (
          <>
            {typeof children === 'function'
              ? children({ so5FixtureSlug: so5Fixture.slug })
              : children}
          </>
        )}
      </Suspense>
    </Root>
  );
};
